import { useEffect, useState } from "react";
import { StatusChip } from "../components/StatusChip.js";
import { api, ApiError } from "../lib/api.js";
import { hashForRoute } from "../lib/hashRoute.js";
import type { BookResponse, Export, ExportWithUrl } from "../lib/types.js";
import { usePolling } from "../lib/usePolling.js";
import { ROUTE_TITLES, formatPageTitle, useDocumentTitle } from "../lib/pageTitle.js";

const btnPrimary =
  "shrink-0 rounded-md bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) " +
  "hover:bg-(--accent-hover) disabled:opacity-50";

const btnGhost =
  "shrink-0 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 " +
  "disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800";

/** The book assembly view (Picture-Book Workflow plan, Task 4.1). Renders every
 *  paged item of a project in reading order with its accepted illustration and
 *  on-page words, and a one-click export that reuses the existing per-project
 *  zip builder (create, then poll `building` -> `ready`, exactly as Exports.tsx). */
export function BookAssembly({ projectId }: { projectId: string }) {
  const [book, setBook] = useState<BookResponse | null>(null);
  // BookResponse carries no project id, so remember which project the book was loaded for.
  const [bookProjectId, setBookProjectId] = useState<string | null>(null);
  useDocumentTitle(
    book && bookProjectId === projectId ? formatPageTitle(book.title) : formatPageTitle(ROUTE_TITLES.book),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [exportId, setExportId] = useState<string | null>(null);
  const [exportRow, setExportRow] = useState<ExportWithUrl | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setBook(await api<BookResponse>(`/api/projects/${projectId}/book`));
      setBookProjectId(projectId);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : "Could not load this book.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Poll the export row from `building` to `ready`, then stop.
  const polling = Boolean(exportId) && exportRow?.status === "building";
  usePolling(
    async () => {
      if (!exportId) return;
      try {
        setExportRow(await api<ExportWithUrl>(`/api/exports/${exportId}`));
      } catch (err) {
        setExportError(err instanceof Error ? err.message : "Could not check the export.");
      }
    },
    { enabled: polling },
  );

  async function handleExport() {
    if (exportBusy) return;
    setExportBusy(true);
    setExportError(null);
    setExportRow(null);
    setExportId(null);
    try {
      const row = await api<Export>(`/api/projects/${projectId}/exports`, { method: "POST", json: {} });
      setExportId(row.exportId);
      setExportRow(row);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Could not start the export.");
    } finally {
      setExportBusy(false);
    }
  }

  async function handleDownload() {
    if (!exportId) return;
    setExportError(null);
    try {
      const detail = await api<ExportWithUrl>(`/api/exports/${exportId}`);
      if (detail.url) window.location.href = detail.url;
      else setExportError("That export is not ready to download yet.");
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Could not fetch the download link.");
    }
  }

  if (loading) {
    return <p className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading...</p>;
  }
  if (error || !book) {
    return <p className="p-6 text-sm text-red-600 dark:text-red-400">{error ?? "Book not found."}</p>;
  }

  const total = book.pages.length;
  const illustrated = book.pages.filter((p) => p.illustration).length;

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{book.title}</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {total} page{total === 1 ? "" : "s"} - {illustrated} of {total} illustrated
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {exportRow?.status === "ready" ? (
            <button type="button" className={btnPrimary} onClick={() => void handleDownload()}>
              Download zip
            </button>
          ) : (
            <button
              type="button"
              className={btnPrimary}
              disabled={exportBusy || exportRow?.status === "building"}
              onClick={() => void handleExport()}
            >
              {exportBusy
                ? "Starting..."
                : exportRow?.status === "building"
                  ? "Building zip..."
                  : "Download book"}
            </button>
          )}
          <a
            href={hashForRoute({ kind: "exports" })}
            className="text-xs text-gray-500 hover:underline dark:text-gray-400"
          >
            Export history
          </a>
        </div>
      </div>

      {exportRow?.status === "failed" && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400">
          The export build failed - try again.
        </p>
      )}
      {exportError && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{exportError}</p>}

      {book.pages.length === 0 ? (
        <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
          No pages yet. Accept the outline and use <em>Expand into pages</em> on its item.
        </p>
      ) : (
        <ol className="mt-6 space-y-8">
          {book.pages.map((page) => (
            <li key={page.itemId} className="border-t border-gray-200 pt-6 first:border-t-0 first:pt-0 dark:border-gray-800">
              <div className="mb-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium text-gray-700 dark:text-gray-300">Page {page.pageNumber}</span>
                <a
                  href={hashForRoute({ kind: "itemDetail", projectId, itemId: page.itemId })}
                  className="hover:underline"
                >
                  {page.title}
                </a>
                {!page.illustration && <StatusChip status={page.status} label={page.status} />}
              </div>
              {page.illustration ? (
                <img
                  src={page.illustration.url}
                  alt={page.pageText || `Page ${page.pageNumber}`}
                  className="w-full rounded-md border border-gray-200 dark:border-gray-800"
                />
              ) : (
                <div className="flex h-40 items-center justify-center rounded-md border border-dashed border-gray-300 text-sm text-gray-400 dark:border-gray-700">
                  No accepted illustration yet
                </div>
              )}
              {page.pageText && (
                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">
                  {page.pageText}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}

      <a
        href={hashForRoute({ kind: "projectDetail", projectId })}
        className={`${btnGhost} mt-8 inline-block`}
      >
        Back to project
      </a>
    </div>
  );
}
